import { router } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import type { CompanyBalanceShare } from "@/components/billing/company-balance-nav"
import { getCableConsumer, subscribeCableChannel } from "@/lib/cable-consumer"

/**
 * Стартовое значение приходит общим пропом на полном визите, дальше остаток
 * двигает канал компании (docs/billing_balance_widget.md §5).
 */
export function useCompanyBalance(
  shared: CompanyBalanceShare | null,
  companyId: number | null,
): CompanyBalanceShare | null {
  const [balance, setBalance] = useState<CompanyBalanceShare | null>(shared)
  const enabled = shared != null && companyId != null
  // Пока сокет лежал, сообщения не копятся: после обрыва цифру берём заново.
  const reconnectRef = useRef(false)

  useEffect(() => {
    setBalance(shared)
  }, [shared])

  useEffect(() => {
    if (!enabled || companyId == null) return

    return subscribeCableChannel(
      getCableConsumer(),
      { channel: "Companies::BalanceChannel", company_id: companyId },
      {
        connected() {
          if (!reconnectRef.current) return
          reconnectRef.current = false
          router.reload({ only: ["company_balance"] })
        },
        disconnected() {
          reconnectRef.current = true
        },
        received(data: unknown) {
          if (!data || typeof data !== "object") return
          const payload = data as Partial<CompanyBalanceShare> & { type?: string }
          if (payload.type !== "company_balance") return
          if (payload.available_microcents == null) return

          setBalance((prev) =>
            prev
              ? {
                  ...prev,
                  balance_microcents: payload.balance_microcents ?? prev.balance_microcents,
                  held_microcents: payload.held_microcents ?? prev.held_microcents,
                  available_microcents: payload.available_microcents ?? prev.available_microcents,
                  accounts_count: payload.accounts_count ?? prev.accounts_count,
                  currencies: payload.currencies ?? prev.currencies,
                }
              : prev,
          )
        },
      },
    )
  }, [companyId, enabled])

  return balance
}
